import { ATTRIBUTION_REPORT_PATH, CLIPBOARD_TOKEN_REG, DOMAIN_API_BASE_MAP, REPORT_TIMEOUT } from './const';
import type { IReportAttributionReply, IReportAttributionReq } from './types';

interface IReportResponse {
  code?: number;
  message?: string;
  data?: IReportAttributionReply;
}

/** 上报结果：token 供业务使用，status / body 供调试 */
export interface IReportResult {
  token: string;
  status: number;
  body: unknown;
}

/**
 * 构建时注入的默认 API 地址。
 * 直接读 import.meta.env 而不引用 request/config，避免 IIFE 包把 axios 链路打进来；
 * IIFE 构建时该值为空，必须通过 apiBase 传入。
 * 用 (import.meta as { env?: ... }) 局部断言而非全局 ImportMetaEnv 声明合并：
 * 消费方（如 apps/main）通常已有自己的 ImportMetaEnv 声明，多份全局声明合并会触发
 * TypeScript 5.8.3 的编译器崩溃（index signature 场景下的 Debug Failure）。
 */
const DEFAULT_API_BASE: string = (import.meta as { env?: { VITE_APP_BASE_URL?: string } }).env?.VITE_APP_BASE_URL || '';

/**
 * 按当前页面域名兜底匹配 API 地址，仅用于 IIFE 独立包场景（该场景 DEFAULT_API_BASE 始终为空）。
 * ESM 包场景下 DEFAULT_API_BASE 已有值，不会走到这里。
 */
function resolveApiBaseByDomain(): string {
  try {
    return DOMAIN_API_BASE_MAP[window.location.hostname] || '';
  } catch {
    return '';
  }
}

/**
 * 上报设备参数并取回剪贴板 token。
 * 不走全局 axios：该接口免登录，且失败时不能弹 toast / 跳登录，
 * 归因失败对用户应完全无感。失败时 token 为空字符串。
 */
export async function reportAttribution(payload: IReportAttributionReq, apiBase?: string): Promise<IReportResult> {
  const base = (apiBase || DEFAULT_API_BASE || resolveApiBaseByDomain()).replace(/\/+$/, '');
  const result: IReportResult = { token: '', status: 0, body: null };
  if (!base) return result;

  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), REPORT_TIMEOUT) : null;
  try {
    const res = await fetch(`${base}${ATTRIBUTION_REPORT_PATH}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      signal: controller?.signal,
      // 免登录接口，不携带 cookie，避免触发凭证模式的 CORS 限制
      credentials: 'omit'
    });
    result.status = res.status;
    const text = await res.text();
    let json: IReportResponse | null = null;
    try {
      json = JSON.parse(text);
    } catch {
      json = null;
    }
    result.body = json ?? text;
    if (!res.ok || !json) return result;
    if (json.code !== undefined && json.code !== 200) return result;
    // 兼容两种返回：业务包装 { code, data } 与直接返回 { clipboard }
    const reply = (json.data ?? json) as IReportAttributionReply | undefined;
    const token = reply?.clipboard || '';
    if (CLIPBOARD_TOKEN_REG.test(token)) result.token = token;
    return result;
  } catch (err) {
    result.body = err instanceof Error ? err.message : String(err);
    return result;
  } finally {
    if (timer) clearTimeout(timer);
  }
}
